import type { ReactNode } from 'react';
import { Tooltip } from '@/components/shared/Tooltip';

interface StatusBadgeProps {
  status: string;
  tooltip?: string;
  // When provided, used as the visible label instead of the humanized
  // `status` string. Lets callers pair a semantic color (`success` /
  // `warning` / `danger` / `neutral`) with arbitrary copy.
  children?: ReactNode;
}

const COLORS: Record<string, string> = {
  // Queued = created but waiting for a worker slot; a calm neutral, not the
  // amber of an actively-running step.
  queued: 'bg-dash-dim/20 text-dash-muted border-dash-dim/40',
  writing: 'bg-warning/20 text-warning border-warning/40',
  reviewing: 'bg-warning/20 text-warning border-warning/40',
  revising: 'bg-warning/20 text-warning border-warning/40',
  approved: 'bg-success/20 text-success border-success/40',
  draft: 'bg-accent/20 text-accent border-accent/40',
  publishing: 'bg-warning/20 text-warning border-warning/40',
  live: 'bg-success/20 text-success border-success/40',
  published: 'bg-success/20 text-success border-success/40',
  failed: 'bg-error/20 text-error border-error/40',
  unpublished: 'bg-dash-dim/20 text-dash-muted border-dash-dim/40',
  archived: 'bg-dash-dim/20 text-dash-muted border-dash-dim/40',
  proposed: 'bg-purple/20 text-purple border-purple/40',
  rejected: 'bg-dash-dim/20 text-dash-muted border-dash-dim/40',
  in_pipeline: 'bg-accent/20 text-accent border-accent/40',
  error: 'bg-error/20 text-error border-error/40',
  warning: 'bg-warning/20 text-warning border-warning/40',
  info: 'bg-accent/20 text-accent border-accent/40',
  success: 'bg-success/20 text-success border-success/40',
  danger: 'bg-error/20 text-error border-error/40',
  neutral: 'bg-dash-dim/20 text-dash-muted border-dash-dim/40',
  // Site lifecycle. `active` is the steady "on" state, so it gets a stronger
  // green fill than the transient success states above to read unmistakably as
  // live (rather than the muted default it used to fall through to).
  active: 'bg-success/25 text-success border-success/50 font-semibold',
  scaffolding: 'bg-warning/20 text-warning border-warning/40',
  scaffold_failed: 'bg-error/20 text-error border-error/40',
  paused: 'bg-dash-dim/20 text-dash-muted border-dash-dim/40',
  decommissioned: 'bg-dash-dim/20 text-dash-muted border-dash-dim/40',
  // Domain / SSL lifecycle (pending/propagating in-progress; active is good).
  pending: 'bg-warning/20 text-warning border-warning/40',
  propagating: 'bg-warning/20 text-warning border-warning/40',
};

export function StatusBadge({ status, tooltip, children }: StatusBadgeProps) {
  const color = COLORS[status] ?? 'bg-dash-dim/20 text-dash-muted border-dash-dim/40';
  const badge = (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-mono uppercase tracking-wide ${color}`}
    >
      {children ?? status.replace(/_/g, ' ')}
    </span>
  );
  if (!tooltip) return badge;
  return <Tooltip text={tooltip}>{badge}</Tooltip>;
}
